import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { DefaultRule } from "../types";

/**
 * Default rule: Generate Suggested Merchant Knowledge
 *
 * On close, schedules a scan of the ticket for knowledge the AI was missing.
 * The action decides whether to scan now or after a quiet period, and skips
 * tickets the AI never touched or a human never resolved.
 *
 * The TICKET_STATUS condition is what lets `rule-prefilter` drop non-close
 * status changes before a workflow is ever started.
 *
 * hidden: true — backend-only system rule.
 */
export const generateSuggestedMerchantKnowledgeRule = {
  id: "default-generate-suggested-merchant-knowledge",
  name: "Generate Suggested Merchant Knowledge",
  description:
    "When a conversation the AI handed off is closed, propose knowledge that would let the AI resolve it next time.",
  hidden: true,
  triggerType: RuleTriggerType.CONVERSATION_STATUS_CHANGED,

  defaultConfig: {
    defaultRuleId: "default-generate-suggested-merchant-knowledge",
    enabled: true,
    // Hidden rule — large sortIndex keeps it out of the way if ever surfaced.
    sortIndex: 10_001,
    configurations: {},
  },

  template: {
    name: "Generate Suggested Merchant Knowledge",
    description:
      "When a conversation the AI handed off is closed, propose knowledge that would let the AI resolve it next time.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_STATUS_CHANGED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["CLOSED"],
              queryOperator: QueryOperator.IS,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          { actionType: RuleActionType.GENERATE_SUGGESTED_MERCHANT_KNOWLEDGE },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.CONTINUE_ON_ERROR,
      },
    ],
  },
} as const satisfies DefaultRule;
